import type { IOperationError } from '@hc-frontend/core-utils-business-operation';
import {
  createErrorOperationResult,
  createSuccessOperationResult,
} from '@hc-frontend/core-utils-business-operation';
import { v1 } from '@hc-frontend/services-ms-teams';

import type { Log } from '../../domain';
import { errorBuilder } from '../../domain';
import type { PerformOperationLogRepository } from '../../domain/repositories';
import type { MsTeamsHookFactoryParams } from './ms-teams-hook-factory.types';

function createPayload(applicationName: string, log: Log): v1.HookPayloadDTO {
  const colors = {
    debug: '808080',
    info: '0000FF',
    warn: 'FFA500',
    error: 'EE4B2B',
  };
  const summary = `Incoming event from [${applicationName} - ${log.context}] of type: [${log.type}]`;

  const payload: v1.HookPayloadDTO = {
    '@type': 'MessageCard',
    '@context': 'http://schema.org/extensions',
    themeColor: colors[log.type],
    summary,
    sections: [
      {
        activityTitle: `${applicationName.toUpperCase()} - ${log.context.toUpperCase()}`,
        activitySubtitle: summary,
        facts: [
          {
            name: 'context',
            value: log.context,
          },
          {
            name: 'type',
            value: log.type,
          },
          {
            name: 'message',
            value: log.message,
          },
        ],
        markdown: true,
      },
    ],
  };

  if (log.data)
    payload.sections.push({
      activitySubtitle: 'Details',
      facts: Object.entries(log.data).map(([name, value]) => ({
        name,
        value: String(value),
      })),
      markdown: true,
    });

  return payload;
}

export function msTeamsHookFactory({
  enabled,
  hookUrl,
  applicationName,
  typesFilters = ['error'],
}: MsTeamsHookFactoryParams): PerformOperationLogRepository {
  return async (log) => {
    try {
      if (!enabled || !typesFilters.includes(log.type))
        return createSuccessOperationResult(undefined);

      const result = await v1.invokeHook(
        hookUrl,
        createPayload(applicationName, log),
      );

      if (result.result === 'success')
        return createSuccessOperationResult(undefined);

      return createErrorOperationResult({
        errors: [
          {
            code: 'failOnPerformOperationLog',
            rootCause: new Error('Error on invoke ms teams hook'),

            info: {
              httpResult: result.result,
              httpCode: result.status,
              httpResponseContent: String(result.body),
            },
          },
        ],
      });
    } catch (e) {
      return createErrorOperationResult({
        errors: errorBuilder({
          add: 'failOnPerformOperationLog',
          e,
        }) as IOperationError<'failOnPerformOperationLog'>[], // TODO: Improve type inference for error builder
      });
    }
  };
}
